import { useEffect, useMemo, useRef, useState } from 'react';

import { ViewProps } from './draw.constants';
import SubActivityPrint from '../sub-activity/sub-activity-print';
import { useActivityAnswersPrintContext } from 'approot/activity-builder/print/answers/activity-answers-print.context';
import useGetStudentAnswerById from 'approot/activity-builder/classroom/use-get-student-answer-by-id';
import useGetTeacherAnswerById from 'approot/answers/use-get-teacher-answer-by-id';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { getPrintImage } from '../shared/logic/get-print-image';
import { hasDrawAnswer } from '../shared/logic/has-draw-answer';
import DrawPrintCanvasAnswers from './draw-print-answers-canvas';
import DrawPrint from './draw-print';
import * as Styled from './draw-print-answers.style';
import { LanguageCodes } from 'global/constants';

const DrawPrintAnswers = (
  props: ViewProps & {
    studentId?: number;
    previewType: string;
    language: LanguageCodes;
    onRenderComplete: () => void;
  }
) => {
  const {
    id,
    subActivityProperties,
    properties,
    studentId,
    previewType,
    language,
    onRenderComplete,
  } = props;
  const timestampRef = useRef<number>(Date.now());
  const [previewRef, setPreviewRef] = useState<HTMLDivElement | null>(null);

  const { contentId, contentType, classId, answers } =
    useActivityAnswersPrintContext(id);

  const answer = useMemo(
    () =>
      studentId ? answers?.find(a => a.studentId === studentId) : answers?.[0],
    [answers, studentId]
  );

  const drawingAnswer = answer?.answer as DrawAnswerFragment;
  const hasDrawingData = drawingAnswer?.hasDrawingData;

  const hasNoDrawingData = !answer?.uuid || !hasDrawingData;
  // this check if the data has been fetched
  const hasCanvasDrawingData = hasDrawAnswer(drawingAnswer);

  const paddingTop =
    (properties.dimensionData.height / properties.dimensionData.width) * 100;

  const imageUrl = getPrintImage(properties.image?.responsive || []);

  const isStudentAnswer = !!studentId;
  // skip if this is a student
  const { data: teacherAnswer, loading: teacherAnswerLoading } =
    useGetTeacherAnswerById({
      skip:
        isStudentAnswer ||
        !answer ||
        hasDrawingData === false ||
        hasCanvasDrawingData,
      uuid: answer?.uuid!,
      classId,
      contentId,
      contentType,
      fetchPolicy: 'cache-and-network',
    });

  // skip this is a teacher
  const { data: studentAnswer, loading: studentAnswerLoading } =
    useGetStudentAnswerById({
      skip:
        !isStudentAnswer ||
        !answer ||
        hasDrawingData === false ||
        hasCanvasDrawingData,
      uuid: answer?.uuid!,
      classId,
      contentId,
      contentType,
      fetchPolicy: 'cache-and-network',
    });

  const isLoading = teacherAnswerLoading || studentAnswerLoading;
  const fetchDrawing = hasCanvasDrawingData
    ? drawingAnswer
    : isStudentAnswer
      ? (studentAnswer?.getStudentAnswerById.answer as DrawAnswerFragment)
      : (teacherAnswer?.getTeacherAnswerById.answer as DrawAnswerFragment);

  useEffect(() => {
    if (hasNoDrawingData) {
      onRenderComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hasNoDrawingData) {
    // no answer found
    return (
      <div key={`${id}-${timestampRef.current}`}>
        <DrawPrint {...props} />
      </div>
    );
  }

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <Styled.DrawPrintAnswers
        style={{
          paddingTop: `${paddingTop}%`,
        }}
      >
        <Styled.DrawPrintAnswersInner role="image" ref={setPreviewRef}>
          {previewRef && answer && fetchDrawing?.drawing && !isLoading && (
            <DrawPrintCanvasAnswers
              key={`${id}-${answer.uuid}-${answer.updatedAt}-${timestampRef.current}`}
              id={`${previewType}-${answer.uuid}`}
              containerEl={previewRef}
              imageUrl={imageUrl}
              dimensionsData={properties.dimensionData}
              answer={fetchDrawing.drawing}
              questionData={properties.draw}
              language={language}
              onRenderComplete={onRenderComplete}
            />
          )}
        </Styled.DrawPrintAnswersInner>
      </Styled.DrawPrintAnswers>
    </SubActivityPrint>
  );
};

export default DrawPrintAnswers;
